import { Alert, Box } from '@mui/material'
import type { FC } from 'react'

import ResetSection from '@/components/ResetSection/ResetSection'
import { ResetableSection } from '@/lib/apollo/mutation/reset-section'
import type { Application } from '@/types/Activity'
import {
  ADVERSE_CREDIT_FRAUD_ESCALATION_WARNING,
  hasAdverseCredit,
} from '@/utils/adverseCredit'
import { hasIdentityData } from '@/utils/identity'
import { hasIncomeData } from '@/utils/income'
import { hasResidentialData } from '@/utils/residential'
import { isCorporate } from '@/utils/subjectHelpers'

interface Props {
  application: Application
  customerId: string
  applicationId: string
  subjectId: string
  showResetWholeSubject: boolean
}

const ResetSections: FC<Props> = ({
  application,
  customerId,
  applicationId,
  subjectId,
  showResetWholeSubject,
}) => {
  const isCorporateLet = isCorporate(application?.context?.journeyType)

  if (isCorporateLet) {
    return (
      <Box sx={{ pt: 2 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          This cannot be done for Corporate Let applications.
        </Alert>
      </Box>
    )
  }

  const hasAdverseCreditOnFile = hasAdverseCredit(application)
  const showIdentityReset = hasIdentityData(application)
  const showResidentialReset = hasResidentialData(application)
  const showIncomeReset = hasIncomeData(application)

  return (
    <Box sx={{ pt: 2 }}>
      {showIdentityReset && (
        <ResetSection
          customerId={customerId}
          applicationId={applicationId}
          subjectId={subjectId}
          buttonText="Reset Identity Section"
          confirmResetMessage="Are you sure you want to reset the identity section?"
          section={ResetableSection.Identity}
          requireReason
        />
      )}

      {showResidentialReset && (
        <Box>
          <ResetSection
            customerId={customerId}
            applicationId={applicationId}
            subjectId={subjectId}
            buttonText="Reset Residential Section"
            confirmResetMessage={
              hasAdverseCreditOnFile
                ? `${ADVERSE_CREDIT_FRAUD_ESCALATION_WARNING} Are you sure you want to continue?`
                : 'Are you sure you want to reset the residential section?'
            }
            section={ResetableSection.Residential}
            requireReason
          />
        </Box>
      )}

      {showIncomeReset && (
        <ResetSection
          customerId={customerId}
          applicationId={applicationId}
          subjectId={subjectId}
          buttonText="Reset Income Section"
          confirmResetMessage="Are you sure you want to reset the income section?"
          section={ResetableSection.Income}
          requireReason
        />
      )}

      {showResetWholeSubject && (
        <Box sx={{ borderTop: '1px solid #000', mt: 2.5, pt: 2.5 }}>
          {hasAdverseCreditOnFile ? (
            <Alert severity="warning">
              This subject has adverse credit on file. Please raise a dev support ticket
              to reset this subject's application.
            </Alert>
          ) : (
            <ResetSection
              customerId={customerId}
              applicationId={applicationId}
              subjectId={subjectId}
              buttonText="Reset Application"
              confirmResetMessage="Are you sure you want to reset all the data on this subject? This action cannot be undone."
              section={ResetableSection.All}
              requireReason
            />
          )}
        </Box>
      )}
    </Box>
  )
}

export default ResetSections
